import {CommonModule} from "@angular/common";
import {Component, OnInit} from "@angular/core";
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {provideNativeDateAdapter} from "@angular/material/core";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatSelectModule} from "@angular/material/select";
import {MatSnackBar} from "@angular/material/snack-bar";
import {environment} from "../../environments/environment";
import {Client} from "../client-overview/dto/client";
import {ClientService} from "../client-overview/service/client.service";
import {SessionService} from "../state/session.service";
import {AgentAccessService} from "./agent-access.service";
import {AgentExportType, buildAgentExportCurl} from "./agent-curl";

@Component({
  selector: 'agent-access-dialog',
  standalone: true,
  templateUrl: './agent-access-dialog.component.html',
  styleUrls: ['./agent-access-dialog.component.scss'],
  imports: [CommonModule, MatButtonModule, MatDatepickerModule, MatDialogModule, MatFormFieldModule,
    MatIconModule, MatInputModule, MatSelectModule, ReactiveFormsModule],
  providers: [provideNativeDateAdapter()]
})
export class AgentAccessDialogComponent implements OnInit {
  readonly earliestAllowedDate = new Date(2025, 8, 1, 0, 0, 0, 0);
  readonly formGroup: FormGroup;
  clients: Client[] = [];
  curlCommand = '';
  generating = false;

  constructor(public readonly dialogRef: MatDialogRef<AgentAccessDialogComponent>,
              private readonly formBuilder: FormBuilder,
              private readonly clientService: ClientService,
              private readonly agentAccessService: AgentAccessService,
              private readonly session: SessionService,
              private readonly snack: MatSnackBar) {
    const defaultRange = this.createDefaultRange();
    this.formGroup = this.formBuilder.group({
      exportType: ['MESSAGES' satisfies AgentExportType, Validators.required],
      clients: [[], Validators.required],
      dateRange: this.formBuilder.group({
        start: [defaultRange.start, Validators.required],
        end: [defaultRange.end, Validators.required]
      }, {validators: this.createDateRangeValidator()})
    });
    this.formGroup.valueChanges.subscribe(() => this.curlCommand = '');
  }

  ngOnInit(): void {
    this.loadClients();
  }

  generateCurl(): void {
    if (!this.session.isAdmin()) {
      this.snack.open('Only administrators can create agent tokens.', 'Close', {duration: 3000});
      return;
    }

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.snack.open('Select at least one client and a valid date range.', 'Close', {duration: 3000});
      return;
    }

    const selectedClients = this.formGroup.get('clients')?.value as Client[];
    const exportType = this.formGroup.get('exportType')?.value as AgentExportType;
    const start = this.dateRangeGroup.get('start')?.value as Date;
    const end = this.dateRangeGroup.get('end')?.value as Date;
    this.generating = true;

    this.agentAccessService.generateToken().subscribe({
      next: ({token}) => {
        this.curlCommand = buildAgentExportCurl({
          apiUrl: environment.API_URL,
          token,
          exportType,
          from: this.startOfDay(new Date(start)),
          to: this.endOfDay(new Date(end)),
          clientUuids: selectedClients.map(client => client.uuid)
        });
        this.generating = false;
      },
      error: (error) => {
        console.error('Failed to create agent token', error);
        this.snack.open('Failed to create agent token.', 'Close', {duration: 3000});
        this.generating = false;
      }
    });
  }

  copyCurl(): void {
    if (!this.curlCommand || !navigator.clipboard?.writeText) {
      this.snack.open('Clipboard is not available in this browser.', 'Close', {duration: 3000});
      return;
    }

    navigator.clipboard.writeText(this.curlCommand)
      .then(() => this.snack.open('Curl command copied.', 'Close', {duration: 2000}))
      .catch(() => this.snack.open('Failed to copy curl command.', 'Close', {duration: 3000}));
  }

  close(): void {
    this.dialogRef.close();
  }

  get dateRangeGroup(): FormGroup {
    return this.formGroup.get('dateRange') as FormGroup;
  }

  get selectedClientLabel(): string {
    const selectedClients = this.formGroup.get('clients')?.value as Client[];
    if (!selectedClients?.length) {
      return 'Select clients';
    }
    if (selectedClients.length === 1) {
      return selectedClients[0].name;
    }
    return `${selectedClients.length} clients`;
  }

  private loadClients(): void {
    this.clientService.getAll().subscribe({
      next: clients => this.clients = clients,
      error: error => {
        console.error('Failed to load clients for agent access', error);
        this.snack.open('Failed to load clients.', 'Close', {duration: 3000});
      }
    });
  }

  private createDateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const start = control.get('start')?.value;
      const end = control.get('end')?.value;
      if (!start || !end) {
        return null;
      }

      const startDate = this.startOfDay(new Date(start));
      const endDate = this.startOfDay(new Date(end));
      if (startDate < this.earliestAllowedDate) {
        return {beforeEarliest: true};
      }
      if (startDate > endDate) {
        return {invalidRange: true};
      }
      return null;
    };
  }

  private createDefaultRange(): {start: Date, end: Date} {
    const end = this.endOfDay(new Date());
    const start = this.startOfDay(new Date(end));
    start.setDate(start.getDate() - 6);
    return {
      start: start < this.earliestAllowedDate ? new Date(this.earliestAllowedDate) : start,
      end
    };
  }

  private startOfDay(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

  private endOfDay(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(23, 59, 59, 999);
    return normalized;
  }
}
